import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Activity, ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setDone(true);
    } catch (e) {
      toast.error("Something went wrong", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6" data-testid="forgot-password-page">
      <Card className="w-full max-w-md p-8 border border-zinc-200 shadow-sm">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-9 w-9 grid place-items-center bg-zinc-950 text-white rounded-md">
            <Activity className="h-4 w-4" strokeWidth={2.25} />
          </div>
          <div className="font-display text-xl font-bold tracking-tight text-zinc-950">
            Acuity<span className="text-zinc-400">/</span>Reports
          </div>
        </div>

        {done ? (
          <div data-testid="forgot-password-success">
            <div className="h-10 w-10 grid place-items-center rounded-full bg-emerald-100 text-emerald-700">
              <MailCheck className="h-5 w-5" />
            </div>
            <h1 className="mt-4 font-display text-2xl font-bold tracking-tight text-zinc-950">Check your inbox</h1>
            <p className="mt-2 text-sm text-zinc-600">
              If an account exists for <span className="font-mono-num">{email}</span>, we've sent a password-reset link.
              It will expire in 1 hour and can only be used once.
            </p>
            <p className="mt-4 text-xs text-zinc-500">
              Didn't receive it? Check your spam folder, or wait a minute and{" "}
              <button
                type="button"
                className="underline hover:text-zinc-900"
                onClick={() => setDone(false)}
                data-testid="forgot-password-resend"
              >
                try again
              </button>
              .
            </p>
            <div className="mt-6">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-950"
                data-testid="forgot-password-back-to-login"
              >
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-950">Forgot password?</h1>
            <p className="mt-1 text-sm text-zinc-600">
              Enter the email address associated with your account and we'll send you a link to choose a new password.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input
                  id="fp-email"
                  data-testid="forgot-password-email-input"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 bg-white"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="forgot-password-submit"
                className="w-full bg-zinc-950 hover:bg-zinc-800 text-white"
              >
                {submitting ? "Sending…" : "Email me a reset link"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-950"
                data-testid="forgot-password-back-to-login"
              >
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
